/**
 * The new-message sound, shared by the dashboard and the widget.
 *
 * ## Synthesised rather than a file
 *
 * Two oscillators and a gain envelope, about forty lines. The alternatives were
 * shipping an audio file — which the widget would have to fetch cross-origin from
 * whichever host served its bundle, adding a request and a CORS surface for a sound
 * — or embedding one as a base64 data URI, which is tens of kilobytes on a bundle
 * whose whole point is being small.
 *
 * A two-note rising interval is used because it reads as "something arrived" rather
 * than "something went wrong". A single beep is ambiguous; a falling interval sounds
 * like an error.
 *
 * ## Autoplay policy
 *
 * A browser will not produce sound until the user has interacted with the page.
 * Every path here has: an agent has clicked into a conversation, a customer has
 * opened the chat and typed. The first attempt can still be refused, and that is why
 * nothing throws — a silent chime is a small degradation, and an unhandled rejection
 * in a message handler is not.
 *
 * The `AudioContext` is created lazily for the same reason: constructing one before
 * any interaction leaves it suspended, and some browsers log a warning about it on
 * every page of the shop.
 */
/** A rising major third — arrival, not alarm. */
const NOTES = [
    { frequency: 660, startsAt: 0, duration: 0.12 },
    { frequency: 880, startsAt: 0.1, duration: 0.18 },
];
function readMuted(storageKey) {
    try {
        return window.localStorage.getItem(storageKey) === "true";
    }
    catch {
        // Storage can be unavailable in private browsing. Defaulting to unmuted is the
        // right direction: the sound is the feature that was asked for.
        return false;
    }
}
export function createChime(options) {
    const volume = options.volume ?? 0.12;
    let context = null;
    let muted = readMuted(options.storageKey);
    function ensureContext() {
        if (context !== null)
            return context;
        /**
         * Both constructors read as optional, deliberately.
         *
         * The DOM types declare `window.AudioContext` as always present, which is more
         * optimistic than reality — older WebKit ships only the prefixed form, and that
         * is a live concern on the iPads and older iPhones a shop's customers use.
         * Narrowing the type here is what lets the runtime check be honest rather than
         * a suppressed lint rule.
         */
        const candidates = window;
        const Constructor = candidates.AudioContext ?? candidates.webkitAudioContext;
        if (Constructor === undefined)
            return null;
        try {
            context = new Constructor();
            return context;
        }
        catch {
            return null;
        }
    }
    return {
        get muted() {
            return muted;
        },
        setMuted(next) {
            muted = next;
            try {
                window.localStorage.setItem(options.storageKey, String(next));
            }
            catch {
                // The preference is lost on reload. Not worth failing over.
            }
        },
        play() {
            if (muted)
                return;
            const audio = ensureContext();
            if (audio === null)
                return;
            try {
                // A context created before the first interaction starts suspended. Resuming
                // is a no-op when it is already running, and is what makes the first chime
                // after a click actually sound.
                if (audio.state === "suspended")
                    void audio.resume().catch(() => undefined);
                const now = audio.currentTime;
                for (const note of NOTES) {
                    const oscillator = audio.createOscillator();
                    const gain = audio.createGain();
                    // A sine has no harmonics, which is what keeps this from sounding like a
                    // notification from a cheap toy.
                    oscillator.type = "sine";
                    oscillator.frequency.value = note.frequency;
                    const startsAt = now + note.startsAt;
                    // An envelope rather than an abrupt start and stop. Switching a gain from
                    // 0 to full instantaneously produces an audible click at the discontinuity.
                    gain.gain.setValueAtTime(0, startsAt);
                    gain.gain.linearRampToValueAtTime(volume, startsAt + 0.01);
                    gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + note.duration);
                    oscillator.connect(gain);
                    gain.connect(audio.destination);
                    oscillator.start(startsAt);
                    oscillator.stop(startsAt + note.duration + 0.02);
                }
            }
            catch {
                // Autoplay refused, or the context was closed underneath us. Silence is an
                // acceptable outcome; an exception inside a message handler is not.
            }
        },
        close() {
            const current = context;
            context = null;
            if (current !== null)
                void current.close().catch(() => undefined);
        },
    };
}
//# sourceMappingURL=chime.js.map